import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import {
  AdminService,
  PendingListing,
  PendingListingsFilters,
} from '../../services/admin.service';

@Component({
  selector: 'app-admin-pending-listings',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './admin-pending-listings.component.html',
  styleUrl: './admin-pending-listings.component.css',
})
export class AdminPendingListingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  listings: PendingListing[] = [];
  selectedListings: Set<string> = new Set();
  loading = true;
  error: string | null = null;

  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 20;

  filters: PendingListingsFilters = {
    category: 'all',
    status: 'pending',
  };

  searchTerm = '';

  categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'vehicles', label: 'Vehicles' },
    { value: 'real_estate', label: 'Real Estate' },
    { value: 'marketplace', label: 'Marketplace' },
    { value: 'jobs', label: 'Jobs' },
  ];

  statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'active', label: 'Active' },
    { value: 'declined', label: 'Declined' },
  ];

  showBulkActions = false;
  bulkProcessing = false;

  showApproveModal = false;
  showDeclineModal = false;
  showBulkApproveModal = false;
  showQuickDeclineModal = false;
  showQuickApproveModal = false;

  approvalNote = '';
  declineReason = '';
  quickDeclineReason = '';
  quickApprovalNote = '';

  processing = false;
  currentDeclineListing: PendingListing | null = null;
  currentApproveListing: PendingListing | null = null;

  messages: {
    success: string | null;
    error: string | null;
  } = {
    success: null,
    error: null,
  };

  constructor(private adminService: AdminService, private router: Router) {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.filters.search = searchTerm || undefined;
        this.currentPage = 1;
        this.loadListings();
      });
  }

  ngOnInit(): void {
    this.loadListings();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadListings(): void {
    this.loading = true;
    this.error = null;

    this.adminService
      .getPendingListings(this.filters, this.currentPage, this.itemsPerPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.listings = response.listings || [];
          this.currentPage = response.pagination.page || 1;
          this.totalPages = response.pagination.pages || 1;
          this.totalItems = response.pagination.total || 0;
          this.loading = false;
          this.clearSelection();
        },
        error: (error) => {
          console.error('Error loading pending listings:', error);
          this.error = error.message || 'Failed to load pending listings';
          this.listings = [];
          this.totalPages = 1;
          this.totalItems = 0;
          this.currentPage = 1;
          this.loading = false;
        },
      });
  }

  onSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.clearSelection();
    this.loadListings();
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.clearSelection();
      this.loadListings();
    }
  }

  onListingSelect(listingId: string, event: Event): void {
    event.stopPropagation();

    if (this.selectedListings.has(listingId)) {
      this.selectedListings.delete(listingId);
    } else {
      this.selectedListings.add(listingId);
    }

    this.updateBulkActionsVisibility();
  }

  onSelectAll(event: Event): void {
    const checkbox = event.target as HTMLInputElement;

    if (checkbox.checked) {
      this.listings.forEach((listing) => {
        this.selectedListings.add(listing.id.toString());
      });
    } else {
      this.clearSelection();
    }

    this.updateBulkActionsVisibility();
  }

  clearSelection(): void {
    this.selectedListings.clear();
    this.updateBulkActionsVisibility();
  }

  updateBulkActionsVisibility(): void {
    this.showBulkActions = this.selectedListings.size > 0;
  }

  isSelected(listingId: string): boolean {
    return this.selectedListings.has(listingId);
  }

  isAllSelected(): boolean {
    return (
      this.listings.length > 0 &&
      this.listings.every((listing) =>
        this.selectedListings.has(listing.id.toString())
      )
    );
  }

  approveSelected(): void {
    if (this.selectedListings.size === 0) return;
    this.showApproveModal = true;
  }

  declineSelected(): void {
    if (this.selectedListings.size === 0) return;
    this.showDeclineModal = true;
  }

  confirmApprove(): void {
    if (this.selectedListings.size === 0 || this.processing) return;

    this.processing = true;
    const listingIds = Array.from(this.selectedListings);

    this.adminService
      .approveListings(listingIds, this.approvalNote)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccessMessage(
            `${response.summary.approved} listing(s) approved successfully`
          );
          this.showApproveModal = false;
          this.approvalNote = '';
          this.processing = false;
          this.clearSelection();
          this.loadListings();
        },
        error: (error) => {
          console.error('Error approving listings:', error);
          this.showErrorMessage(error.message || 'Failed to approve listings');
          this.processing = false;
        },
      });
  }

  confirmDecline(): void {
    if (
      this.selectedListings.size === 0 ||
      this.processing ||
      !this.declineReason.trim()
    )
      return;

    this.processing = true;
    const listingIds = Array.from(this.selectedListings);

    this.adminService
      .declineListings(listingIds, this.declineReason.trim())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          let message = `${response.summary.declined} listing(s) declined successfully`;

          if (response.summary.relist_credits_refunded > 0) {
            message += `. ${response.summary.relist_credits_refunded} free relist credit(s) automatically refunded to users.`;
          }

          const refundedResults = response.results.filter(
            (r) => r.relist_refunded
          );
          if (refundedResults.length > 0) {
            refundedResults.map((r) => ({
              listingId: r.id,
              refundSource: r.refund_info?.title,
              category: r.refund_info?.category,
            }));
          }

          this.showSuccessMessage(message);
          this.showDeclineModal = false;
          this.declineReason = '';
          this.processing = false;
          this.clearSelection();
          this.loadListings();
        },
        error: (error) => {
          console.error('Error declining listings:', error);
          this.showErrorMessage(error.message || 'Failed to decline listings');
          this.processing = false;
        },
      });
  }

  bulkApproveAll(): void {
    this.showBulkApproveModal = true;
  }

  confirmBulkApprove(): void {
    if (this.bulkProcessing) return;

    this.bulkProcessing = true;

    this.adminService
      .bulkApproveAll(this.filters.category || 'all', this.approvalNote)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccessMessage(
            `${response.summary.approved} listing(s) approved in bulk operation`
          );
          this.showBulkApproveModal = false;
          this.approvalNote = '';
          this.bulkProcessing = false;
          this.clearSelection();
          this.loadListings();
        },
        error: (error) => {
          console.error('Error in bulk approve:', error);
          this.showErrorMessage(
            error.message || 'Failed to bulk approve listings'
          );
          this.bulkProcessing = false;
        },
      });
  }

  quickApprove(listing: PendingListing): void {
    if (
      (listing.status !== 'pending' && listing.status !== 'declined') ||
      this.processing
    )
      return;

    this.currentApproveListing = listing;
    this.showQuickApproveModal = true;
  }

  confirmQuickApprove(): void {
    if (!this.currentApproveListing || this.processing) return;

    this.processing = true;

    this.adminService
      .approveListings(
        [this.currentApproveListing.id.toString()],
        this.quickApprovalNote
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.showSuccessMessage(
            `Listing "${
              this.currentApproveListing!.title
            }" approved successfully`
          );
          this.processing = false;
          this.closeQuickApproveModal();
          this.loadListings();
        },
        error: (error) => {
          console.error('Error approving listing:', error);
          this.showErrorMessage(error.message || 'Failed to approve listing');
          this.processing = false;
        },
      });
  }

  closeQuickApproveModal(): void {
    this.showQuickApproveModal = false;
    this.quickApprovalNote = '';
    this.currentApproveListing = null;
  }

  quickDecline(listing: PendingListing): void {
    if (
      (listing.status !== 'pending' && listing.status !== 'active') ||
      this.processing
    )
      return;

    this.currentDeclineListing = listing;
    this.showQuickDeclineModal = true;
  }

  confirmQuickDecline(): void {
    if (
      !this.currentDeclineListing ||
      this.processing ||
      !this.quickDeclineReason.trim() ||
      this.quickDeclineReason.trim().length < 20
    )
      return;

    this.processing = true;

    this.adminService
      .declineListings(
        [this.currentDeclineListing.id.toString()],
        this.quickDeclineReason.trim()
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          let message = `Listing "${
            this.currentDeclineListing!.title
          }" declined successfully`;

          const result = response.results.find(
            (r) => r.id === this.currentDeclineListing!.id.toString()
          );
          if (result?.relist_refunded && result.refund_info) {
            message += `. Free relist credit refunded from "${result.refund_info.title}".`;
          }

          this.showSuccessMessage(message);
          this.processing = false;
          this.closeQuickDeclineModal();
          this.loadListings();
        },
        error: (error) => {
          console.error('Error declining listing:', error);
          this.showErrorMessage(error.message || 'Failed to decline listing');
          this.processing = false;
        },
      });
  }

  closeQuickDeclineModal(): void {
    this.showQuickDeclineModal = false;
    this.quickDeclineReason = '';
    this.currentDeclineListing = null;
  }

  isQuickDeclineReasonValid(): boolean {
    return this.quickDeclineReason.trim().length >= 20;
  }

  viewListing(listing: PendingListing): void {
    const subcategory = listing.subcategory.replace(/\s+/g, '-').toLowerCase();
    this.router.navigate(['/listing-details', subcategory, listing.id]);
  }

  goBack(): void {
    this.router.navigate(['/admin/dashboard']);
  }

  formatDate(dateString: string): string {
    return this.adminService.formatDate(dateString);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'active':
        return 'status-active';
      case 'declined':
        return 'status-declined';
      default:
        return 'status-default';
    }
  }

  getListingImage(listing: PendingListing): string {
    return (
      listing.first_image ||
      '/assets/images/listing-placeholder/no-image-placeholder.png'
    );
  }

  closeApproveModal(): void {
    this.showApproveModal = false;
    this.approvalNote = '';
  }

  closeDeclineModal(): void {
    this.showDeclineModal = false;
    this.declineReason = '';
  }

  closeBulkApproveModal(): void {
    this.showBulkApproveModal = false;
    this.approvalNote = '';
  }

  private showSuccessMessage(message: string): void {
    this.messages.success = message;
    this.messages.error = null;
    setTimeout(() => (this.messages.success = null), 5000);
  }

  private showErrorMessage(message: string): void {
    this.messages.error = message;
    this.messages.success = null;
    setTimeout(() => (this.messages.error = null), 8000);
  }

  dismissMessage(type: 'success' | 'error'): void {
    this.messages[type] = null;
  }

  getPendingCount(): number {
    return this.listings.filter((listing) => listing.status === 'pending')
      .length;
  }

  getSelectedCount(): number {
    return this.selectedListings.size;
  }

  canPerformBulkActions(): boolean {
    return this.getPendingCount() > 0;
  }

  onRefresh(): void {
    this.currentPage = 1;
    this.clearSelection();
    this.loadListings();
  }

  trackByListingId(index: number, listing: PendingListing): number {
    return listing.id;
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  shouldShowEllipsis(position: 'start' | 'end'): boolean {
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (position === 'start') {
      return this.currentPage > halfVisible + 1 && this.totalPages > maxVisible;
    } else {
      return (
        this.currentPage < this.totalPages - halfVisible &&
        this.totalPages > maxVisible
      );
    }
  }

  getDeclineReason(listing: PendingListing): string {
    return this.adminService.getDeclineReason(listing);
  }
}
